import React, { useState, useEffect, useContext, Children } from 'react';
import { useNavigate } from 'react-router-dom';

import Box from '@mui/material/Box';
import { alpha } from "@mui/material";
import Tooltip from '@mui/material/Tooltip';
import { DataGrid } from '@mui/x-data-grid';
import LoadingButton from '@mui/lab/LoadingButton';
import { randomId } from '@mui/x-data-grid-generator';
import CommentIcon from '@mui/icons-material/Comment';
import LinearProgress from '@mui/material/LinearProgress';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

import { ContextBlock } from 'context/ContextBlock';
import { ContextFilter } from 'context/ContextFilter';
import ConfirmDialog from 'components/object/ConfirmDialog';
import DigiformaStaffingBadge, { isDigiformaStaffed } from 'components/object/DigiformaStaffingBadge';

import moment from 'moment';
import 'styles/Popin.css';
import 'styles/dataGrid.css';
import axios from 'api/axios';
import { saveAs } from 'file-saver';
import logoDigiForma from 'imgs/logo-digiforma.svg';

const TrainingList = (props) => {
  //config
  const navigate = useNavigate();

  //URL API
  const TRAINING_LIST_URL = 'training/list';
  const TRAINING_EXPORT_URL = 'training/export';
  const TRAINING_THEMATIC_LIST_URL = 'training/thematic/list';

  //params context
  const {
    setOpenSnackBar,
    setSnackBarType,
    setSnackBarMessage,
  } = useContext(ContextBlock);

  //context
  const {
    filterName,
    filterModel,
    dateSessionStart,
    intraInter,
    status,

    rows, setRows,
    setTotalRows,
    loadingGrid, setLoadingGrid,
    setDataGridLoaded 
  } = useContext(ContextFilter);

  //states
  const [downloadExport, setDownloadExport] = useState(false);
  const [confirmExportOpen, setConfirmExportOpen] = useState(false);


  
  //effects
  useEffect(() => {
    getTrainingList();
  }, []);


  //functions
  function CustomNoRowsOverlay() {
    return (<div>Aucune données</div>);
  }

  const handleExport = async (event) => {
    setConfirmExportOpen(true);
  }

  const handleRowClick = (params) => {
    var page = "recherche";
    if(params.row.status.label !== "A traiter"){
      page = "resultat";
    }
    navigate(`/formation/${page}?id=${params.row.id}&key=${randomId()}`);
  };

  const RenderCode = (props) => {
    const { value, row } = props;

    var txt = '';
    if (row.qualityExpectations) txt += '- ' + row.qualityExpectations + '\n\r';
    if (row.qualitySuccessConditions) txt += '- ' + row.qualitySuccessConditions;

    var com = '';
    if (txt.length > 0) {
      com = (<Tooltip
        title={<span style={{ whiteSpace: 'pre-line' }}>{txt}</span>}
        placement="right">
        <CommentIcon fontSize="small" />
      </Tooltip>
      );
    }
    
    return <>{value}{com}</>;

    /*if (row.inter) {
      return (
        <>
          <div style={{ height: '20px' }}>{value}{com}</div>
          <div style={{ height: '20px' }}>{row.customer}</div>
        </>
      );
    } else {
      return <>{value}{com}</>;
    }*/
  };

  const RenderColor = (props) => {
    const classColor = props.row?.inter === true ? 'purple' : 'orange'; 
    return <div className={`dataGridRenderCellColor ${classColor}`}></div>;
  };

  const RenderSubsessions = (props) => {
    if (props.row.subsessions.length > 0) {
      const trainingSessions = Array.from(
        new Map(props.row.subsessions.map(item => [item.id_training_session, item])).values()
      );
      return (
        <div className="tableChip">
          <div className="chipCell">
            {Children.toArray(trainingSessions.map((subsession, index) => {
              let color = subsession.id_digiforma_program ? '#00561b' : '#ff8c00';

              const displayName = subsession.program_name || subsession.name;
              return (
              <Tooltip
                title={displayName}
                placement="bottom-start"
              >
                <Box component="div" className="chipText"
                  sx={{
                    p: 1,
                    border: 1,
                    color: color,
                    borderColor: alpha(color, 0.6),
                    backgroundColor: alpha(color, 0.1),
                    maxWidth: '100%',
                    flexShrink: 1,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    minWidth: 0,
                  }}
                >
                {displayName}
                </Box>
              </Tooltip>
              );
            }))}
          </div>
        </div>
      );
    }
    else return (<></>);
  };

  // One entry per Digiforma module (subsessions holds one row per saved date)
  const getUniqueModules = (subsessions) => {
    return Array.from(
      new Map((subsessions || []).map(item => [item.id_training_session, item])).values()
    ).map((subsession) => ({
      name: subsession.program_name || subsession.name,
      digiformaStaffing: subsession.digiformaStaffing
    }));
  };

  const RenderDigiformaStaffing = (props) => {
    return <DigiformaStaffingBadge modules={getUniqueModules(props.row.subsessions)} infoIcon />;
  };

  const RenderStatus = (props) => {
    const { value } = props;

    const label = value?.label ? value.label : 'A traiter';
    const className = !value?.color ? 'default' : '';
    const sx = !value?.color ? {} : {
      border: 1,
      color: value.color,
      borderColor: alpha(value.color, 0.6),
      backgroundColor: alpha(value.color, 0.1)
    };

    return (
      <div className="tableChip">
        <div className="chipCell">
          <Box component="div" className={`chipText alone ${className}`} sx={sx}>
            {label}
          </Box>
        </div>
      </div>
    );
  };

  const formatManager = (manager) => {
    var formattedManager = '';
    if (manager?.lastname) {
      formattedManager += manager?.lastname.toUpperCase();
    }

    if (manager?.firstname) {
      if(formattedManager !== ''){
        formattedManager += ' ';
      }
      formattedManager += manager?.firstname;
    }
    
    return formattedManager;
  };

  const RenderDigiForma = (props) => {
    const { value } = props;

    return (
      <div className="iconLink" onClick={(e) => {e.stopPropagation();}}>
        <div className="linkButton">
          <a href={`https://app.digiforma.com/training_sessions/${value}`} target="_blank">
            <div className="buttonIcon">
              <img src={logoDigiForma} alt="DigiForma" />
            </div>
          </a>
        </div>
      </div>
    );
  };

  const RenderDate = (props) => {
    const { value } = props;
    //Some trainings have no dates yet in Digiforma
    if (!value) return '';
    return (moment(value).format("DD/MM/YYYY"));
  };

  // Alphabetical, case and accent insensitive ("Échéance" next to "Echéance")
  const compareLabels = (label1, label2) => (label1 ?? '').localeCompare(label2 ?? '', 'fr', { sensitivity: 'base' });

  const rowsFromApiFormat = (data) => {
    const dataFormat = {};
    if (!!data !== false) {
      dataFormat.id = data.id;
      dataFormat.code = data.code;
      dataFormat.inter = data.inter;
      dataFormat.digiFormaId = data.id;
      dataFormat.manager = data.manager;
      dataFormat.subsessions = data.subsessions;
      dataFormat.digiformaStaffedCount = getUniqueModules(data.subsessions)
        .filter((module) => isDigiformaStaffed(module.digiformaStaffing)).length;
      dataFormat.status = data?.status ? data.status : {};
      dataFormat.placeInfo = data.placeInfo ? data.placeInfo?.city : '';
      dataFormat.zipCode = data.placeInfo ? data.placeInfo?.cityCode : '';
      dataFormat.endDate = data.endDate ? new Date(Date.parse(data.endDate)) : null;
      dataFormat.startDate = data.startDate ? new Date(Date.parse(data.startDate)) : null;
      dataFormat.customer = data.customers?.length > 0 ? data.customers[0]?.entity?.name : '';

      dataFormat.qualityExpectations = data.qualityExpectations;
      dataFormat.qualitySuccessConditions = data.qualitySuccessConditions;

      //
      dataFormat.filtering = dataFormat.code + ' ';
      dataFormat.filtering += dataFormat.customer + ' ';
      dataFormat.filtering += dataFormat.placeInfo + ' ';
      dataFormat.filtering += dataFormat.manager?.lastname + ' ';
      dataFormat.filtering += dataFormat.manager?.firstname + ' ';
      if (dataFormat.subsessions?.length > 0) {
        dataFormat.subsessions.forEach((subsession) => {
          dataFormat.filtering += subsession.name + ' ';
        });
      }

      if(dataFormat.manager){
        dataFormat.humanReadableManager = formatManager(dataFormat.manager);
      }
    }

    return dataFormat;
  }

  const getTrainingList = async () => {
    try {
      setLoadingGrid(true);

      //config call API
      const config = {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        }
      }
      const response = await axios.get(TRAINING_LIST_URL, config);

      //set data
      var idRow = 0;
      var rowsFromApi = [];

      if (response.data?.data?.trainingSessions?.length > 0) {
        for (var i = 0; i < response.data?.data?.trainingSessions?.length; i++) {
          idRow = rowsFromApi.length;
          rowsFromApi[idRow] = rowsFromApiFormat(response.data?.data.trainingSessions[i]);
        }
      }

      //init rows
      setRows(rowsFromApi);
      setTotalRows(rowsFromApi);
      if (props.initFilterMissions) props.initFilterMissions(rowsFromApi);
    } catch (err) {
      console.log(err);
      if (err.response?.status === 401) {
        navigate('/deconnexion');
      }
      else {
        setSnackBarMessage('Erreur technique! Veuillez réessayer ou contacter notre support.');
        setSnackBarType('error');
        setOpenSnackBar(true);
      }
    } finally {
      setLoadingGrid(false);
      setDataGridLoaded(true);
    }
  }

  const getTrainingListExport = async (e) => {
    try {
      const filter = JSON.parse(localStorage.getItem(filterName));
      const filterData = {
        dateSessionStart: dateSessionStart,
        status: status && status?.length > 0 ? status : null,
        searchTxt: filter && filter?.search?.length > 0 ? filter.search : null,
        intraInter: intraInter !== '' && typeof intraInter === 'boolean' ? intraInter : null
      };

      setDownloadExport(true);
      var config = {
        url: TRAINING_EXPORT_URL,
        responseType: 'arraybuffer',
        method: "post",
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
          'Content-Type': 'application/json'
        },
        data: filterData
      };
      const response = await axios(config);

      // Let the user save the file.
      var blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(blob, 'Liste-Formations_' + moment().format('YYYYMMDD-HHmmss') + '.xlsx');
    } catch (err) {
      if (err.response?.status === 401) {
        navigate('/deconnexion');
      } else {
        setSnackBarMessage("Erreur technique! Veuillez réessayer ou contacter notre support.");
        setSnackBarType("error");
        setOpenSnackBar(true);
      }
    } finally {
      setDownloadExport(false);
    }
  }

  //xGrid
  const columns = [
    { field: 'inter', headerName: 'Inter', hideable: true },
    { field: 'filtering', headerName: 'Data for search', hideable: true },
    { field: 'qualityExpectations', headerName: 'Expectations', hideable: true },
    { field: 'qualitySuccessConditions', headerName: 'Success Conditions', hideable: true },
    { field: 'id', headerName: '', sortable: false, width: 8, minWidth: 8, maxWidth: 8, padding: 0,
      renderCell: (params) => {
        return (
          <div className="tableChip">
            <div className="chipCell">{RenderColor(params)}</div>
          </div>
        );
      }
    },
    { field: 'code', headerName: 'Code interne', width: 145,
      renderCell: (params) => {
        return (
          <div className="tableChip">
            <div className="chipCell">{RenderCode(params)}</div>
          </div>
        );
      }
    },
    { field: 'startDate', headerName: 'Du', width: 110,
      renderCell: (params) => {
        return (
          <div className="tableChip">
            <div className="chipCell">{RenderDate(params)}</div>
          </div>
        );
      }
    },
    { field: 'endDate', headerName: 'Au', width: 110,
      renderCell: (params) => {
        return (
          <div className="tableChip">
            <div className="chipCell">{RenderDate(params)}</div>
          </div>
        );
      }
    },
    { field: 'customer', headerName: 'Entreprise', flex: 2, width: 170,
      renderCell: (params) => {
        return (
          <div className="tableChip">
            <div className="chipCell"><span>{params.value}</span></div>
          </div>
        );
      }
    },
    { field: 'placeInfo', headerName: 'Lieu', flex: 1, width: 50,
      renderCell: (params) => {
        return (
          <div className="tableChip">
            <div className="chipCell">{params.value}</div>
          </div>
        );
      }
    },
    { field: 'zipCode', headerName: 'CP', flex: 1, width: 20,
      renderCell: (params) => {
        return (
          <div className="tableChip">
            <div className="chipCell">{params.value}</div>
          </div>
        );
      }
    },
    { field: 'subsessions', headerName: 'Modules', flex: 3, width: 300,
      // Sort on the module names as displayed (first module, then second...)
      sortComparator: (subsessions1, subsessions2) => compareLabels(
        getUniqueModules(subsessions1).map((module) => module.name).join(' '),
        getUniqueModules(subsessions2).map((module) => module.name).join(' ')
      ),
      renderCell: (params) => {
        return (
          <div className="tableChip">
            <div className="chipCell">{RenderSubsessions(params)}</div>
          </div>
        );
      }
    },
    { field: 'digiformaStaffedCount', headerName: 'Staffing DF', width: 100, align: 'center', headerAlign: 'center',
      renderCell: (params) => {
        return (
          <div className="tableChip">
            <div className="chipCell" style={{ textAlign: 'center' }}>{RenderDigiformaStaffing(params)}</div>
          </div>
        );
      }
    },
    { field: 'humanReadableManager', headerName: 'Commercial', width: 170,
      renderCell: (params) => {
        return (
          <div className="tableChip">
            <div className="chipCell">{params.value}</div>
          </div>
        );
      }
    },
    { field: 'status', headerName: 'Statut', width: 120, align: 'center',
      // The cell value is a status object: sort on its label, as displayed ("A traiter" when there's no status)
      sortComparator: (status1, status2) => compareLabels(status1?.label || 'A traiter', status2?.label || 'A traiter'),
      renderCell: (params) => {
        return (
          <div className="tableChip">
            <div className="chipCell">{RenderStatus(params)}</div>
          </div>
        );
      }
    },
    { field: 'digiFormaId', headerName: '', sortable: false, align: 'center', width: 50,
      renderCell: (params) => {
        return (
          <div className="tableChip">
            <div className="chipCell">{RenderDigiForma(params)}</div>
          </div>
        );
      }
    },
  ];


  return (
    <div className="content-list">
      <DataGrid 
        sx={{
          height: '100%',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          // No focus frame on clicked cells / headers — it has no use in this list
          '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
            outline: 'none',
          },
          '& .MuiDataGrid-main': {
            display: 'flex',
            flexDirection: 'column',
            flex: '1',
            minHeight: 0,
          },
          '& .MuiDataGrid-virtualScroller': {
            flex: '1',
            minHeight: 0,
            overflow: 'auto',
          },
          '& .MuiDataGrid-footerContainer': {
            flex: '0 0 auto',
            flexShrink: 0,
          },
        }}
        className="dataGrid HomeListDatagrid"
        components={{
          LoadingOverlay: LinearProgress,
          NoRowsOverlay: CustomNoRowsOverlay
        }}
        initialState={{
          pagination: { paginationModel: { pageSize: 15 } },
          sorting: {
            sortModel: [{ field: 'startDate', sort: 'desc' }],
          },
        }}
        rows={rows}
        columns={columns}
        loading={loadingGrid}
        filterModel={filterModel}
        onRowClick={handleRowClick}
        pageSizeOptions={[8, 15, 25, 50, 100]}
        rowBuffer={15}
        pagination
        disableColumnMenu
        disableColumnFilter
        disableSelectionOnClick
        columnVisibilityModel={{
          // Hide columns status and traderName, the other columns will remain visible
          inter: false,
          filtering: false,
          qualityExpectations: false,
          qualitySuccessConditions: false,
        }}
        getRowClassName={(params) => {
          return 'dataGridRowClass HomeTrainingListRow';
        }}
        getRowHeight={() => 'auto'}
      />

      {rows.length > 0 &&
        <div id="listExport">
          <LoadingButton
            size="small"
            color="success"
            variant="contained"
            loading={downloadExport} loadingPosition="start"
            startIcon={<FileDownloadIcon />}
            onClick={handleExport}
          >
            Exporter
          </LoadingButton>
        </div>
      }

      <ConfirmDialog
        notError={true}
        title={"l'export des formations ?"}
        open={confirmExportOpen}
        setOpen={setConfirmExportOpen}
        onConfirm={getTrainingListExport}
      >
        {"Voulez-vous vraiment exporter les formations ?"}
      </ConfirmDialog>
    </div>
  );
}

export default TrainingList;